export const assessmentData = {
  title: "ACCESSIBILITY BUILD-A-PLAN TOOL",
  subtitle: "Are You Ready for the 2030 Accessibility Moonshot?",
  
  introduction: {
    stats: [
      { value: "73M", label: "Baby Boomers will be over 65" },
      { value: "43%", label: "Will identify as having a disability" },
      { value: "288M", label: "People directly impacted by disability" }
    ],
    content: [
      "Most destinations have accessible features but they're not communicating them. Disabled travelers spend 13 hours researching a trip, and 78% of that time is hunting for accessibility information destinations are not promoting them.",
      "This assessment isn't a test. It's a mirror.",
      "This assessment tool is designed to help develop a 2030 plan that is paced around your budget and bandwidth and built around the current assets you already have."
    ]
  },

  sections: [
    {
      id: 1,
      name: "YOUR ACCESSIBILITY SNAPSHOT",
      tagline: "Your Accessibility Snapshot",
      questions: [
        {
          id: 1,
          type: "single",
          question: "Accessibility Commitment Level",
          subtext: "What stage are you in?",
          reveal: "Your current operational maturity",
          options: [
            { id: "A", text: "Awakening — We know accessibility matters, but it hasn't been formalized or funded yet", profile: "awakening" },
            { id: "B", text: "Active — We have some initiatives, but they're scattered across departments", profile: "awakening" },
            { id: "C", text: "Advanced — We have formal strategy + resources, but struggle with visibility", profile: "momentum" },
            { id: "D", text: "Leading — Accessibility is core to our brand; we promote, measure, and iterate", profile: "advanced" }
          ]
        },
        {
          id: 2,
          type: "compound",
          question: "Bandwidth & Budget Reality",
          subtext: "How much capacity do you actually have?",
          reveal: "Your constraint (time vs. money) determines your path forward",
          subQuestions: [
            {
              id: "bandwidth",
              label: "Hours per month (if you're the accessibility point of contact):",
              type: "single",
              options: [
                { id: "1-5", text: "1-5 hours" },
                { id: "6-15", text: "6-15 hours" },
                { id: "16-30", text: "16-30 hours" },
                { id: "31-50", text: "31-50 hours" },
                { id: "50+", text: "50+ hours" }
              ]
            },
            {
              id: "budget",
              label: "Annual budget available:",
              type: "single",
              options: [
                { id: "under5k", text: "Under $5K" },
                { id: "5k-15k", text: "$5K-$15K" },
                { id: "15k-30k", text: "$15K-$30K" },
                { id: "30k-50k", text: "$30K-$50K" },
                { id: "over50k", text: "Over $50K" }
              ]
            }
          ]
        },
        {
          id: 3,
          type: "role",
          question: "Your Role / Title",
          subtext: "What is your role at your organization?",
          reveal: "This helps us understand how to integrate accessibility into your existing workflow",
          options: [
            { id: "exec_director", text: "Executive Director/CEO" },
            { id: "marketing", text: "Marketing/Communications" },
            { id: "tourism_dev", text: "Tourism Development" },
            { id: "convention", text: "Convention Meeting Sales" },
            { id: "dei", text: "DEI/Sustainability" },
            { id: "destination_exp", text: "Destination Experience" },
            { id: "community", text: "Community Engagement/Partnerships/Membership" },
            { id: "stewardship", text: "Stewardship" },
            { id: "other", text: "Other" }
          ]
        }
      ]
    }
  ],

  profiles: {
    awakening: {
      id: "awakening",
      title: "The Awakening",
      color: "#3B82F6",
      pattern: "Early stage — building awareness and alignment",
      summary: "You know accessibility matters but lack clarity, resources, or buy-in to make it real. Internal alignment is the first blocker. Budget is likely tight; you need low-cost wins to build momentum and show leadership what's possible.",
      currentState: [
        "Accessibility is recognized but not systematized",
        "Initiatives are ad-hoc or reactive",
        "No unified strategy across teams",
        "Limited budget or unclear ROI"
      ],
      whyFits: "You need proof of concept, not massive infrastructure. Start with quick wins to build momentum and create the internal business case for larger investment.",
      timeline: "Start immediately; demonstrate results by Q3 2026",
      ctas: [
        { text: "Schedule Advisory Call (Free, 30 min)", url: "https://calendar.app.google/wg7PUmQf22YPzEdx5", primary: true }
      ]
    },
    momentum: {
      id: "momentum",
      title: "Building Momentum",
      color: "#10B981",
      pattern: "Active stage — connecting the dots",
      summary: "You have initiatives in place but they're fragmented or invisible. You know what you have, but you're not selling it effectively. Budget exists; you need a coordinated strategy and marketing approach that ties it all together.",
      currentState: [
        "Some accessibility infrastructure exists",
        "Efforts are scattered across departments",
        "Partners don't know how to talk about what they offer",
        "Marketing visibility is low or inconsistent"
      ],
      whyFits: "You have the pieces; you need integration and visibility. An audit of what you have, team training, and intentional positioning will unlock your next level.",
      timeline: "Launch refreshed marketing by summer 2026; measure results by Q4 2026",
      ctas: [
        { text: "Schedule Advisory Call (Free, 30 min)", url: "https://calendar.app.google/wg7PUmQf22YPzEdx5", primary: true }
      ]
    },
    advanced: {
      id: "advanced",
      title: "Advanced / Leading",
      color: "#FBBF24",
      pattern: "Leading stage — scaling and sustaining",
      summary: "You have a framework and genuine commitment. Your challenge is execution consistency and scaling impact across your entire partner network. You're ready for transformation and need partnership + accountability.",
      currentState: [
        "Formal accessibility strategy exists",
        "Dedicated resources and leadership buy-in",
        "Clear vision for 2030",
        "Challenge: Scaling & consistency across all partners"
      ],
      whyFits: "You need to move from good intentions to systematic excellence. Partnership helps you scale across your network, develop thought leadership, and become a recognized national leader.",
      timeline: "Launch coordinated rollout across network by spring 2027",
      ctas: [
        { text: "Schedule Advisory Call (Free, 45 min)", url: "https://calendar.app.google/wg7PUmQf22YPzEdx5", primary: true }
      ]
    },
    uncertain: {
      id: "uncertain",
      title: "Uncertain / Blocked",
      color: "#9CA3AF",
      pattern: "Exploratory stage — finding the right path",
      summary: "You see the opportunity but internal barriers are real—whether it's budget constraints, competing priorities, leadership skepticism, or complexity. You need external credibility and a clear, tailored roadmap to unlock internal investment.",
      currentState: [
        "Awareness of the opportunity exists",
        "Specific barriers are holding you back (cost, complexity, perception, execution)",
        "Unclear how to make the business case internally",
        "Need external expertise to move forward"
      ],
      whyFits: "Your situation is unique, and cookie-cutter solutions won't work. A conversation lets us understand your specific blockers and recommend exactly what you need.",
      timeline: "Schedule within 2 weeks; clarity on path forward within 30 days",
      ctas: [
        { text: "Schedule Advisory Call (Free, 30 min)", url: "https://calendar.app.google/wg7PUmQf22YPzEdx5", primary: true }
      ]
    }
  },

  whatHappensNext: [
    { step: 1, text: "You submit the assessment", time: "~2 minutes" },
    { step: 2, text: "We send your results + personalized recommendation", time: "Instantly" },
    { step: 3, text: "We reach out to schedule a call", time: "If you selected \"interested\"" },
    { step: 4, text: "You get the right partnership for your situation", time: "Tailored, not oversold" }
  ],

  credibility: {
    stats: [
      "We work with 50+ destinations & DMOs nationwide",
      "Our clients report 18-25% increase in accessibility-seeking visitor bookings",
      "Partners like Visit Florida, Mesa AZ, North Alabama are already leading with our framework",
      "We're not consultants selling solutions; we're operators who understand your constraints",
      "We design for all disabilities—mobility, sensory, cognitive, invisible, intersectional"
    ],
    closing: "The Longevity Economy is here. The question is: Are you in?"
  },

  contact: {
    email: "jake@travelability.net",
    text: "Questions? Email jake@travelability.net"
  }
};

export const calculateProfile = (answers) => {
  // Primary signal comes from Q1
  const q1 = answers[1];
  if (q1?.profile) return q1.profile;
  return 'awakening';
};

export const getTotalQuestions = () => {
  return assessmentData.sections.reduce((sum, section) => sum + section.questions.length, 0);
};
